import { ExportedMessageRepository } from "@assistant-ui/react";
import { ClientLogger } from "@/lib/clientLogger";
import { ContextWindowSnapshot } from "../stores/contextBudgetStore";
import { usePraetorRevisionStore } from "../stores/praetorRevisionStore";
import {
  ThreadHistoryItem,
  ThreadNotFoundError,
  mapHistoryItemToThreadMessage,
} from "./threadHistoryProvider";
import { hydrateApprovalStoreFromHistory } from "./approvalHydration";

export interface LoadThreadMessagesDeps {
  apiAuthFetch: (input: string, init?: RequestInit) => Promise<Response>;
  clearLastEventId: (remoteId: string) => void;
  getLastViewedBranch: (remoteId: string) => string | null;
  setLastViewedBranch: (remoteId: string, leafId: string | null) => void;
  setContextWindow: (remoteId: string, snapshot: ContextWindowSnapshot | null) => void;
  fetchGeneratedDocuments: (ids: string[]) => Promise<unknown>;
  clearLoadingFlag: () => void;
  /** Sleep helper — injected so tests can swap in `vi.useFakeTimers()`-aware variant. */
  sleep?: (ms: number) => Promise<void>;
}

const LOAD_RETRY_DELAY_MS = 1500;
export const MAX_LOAD_ATTEMPTS = 2;

/**
 * Fetch and hydrate a thread for assistant-ui's history adapter.
 *
 * Resilience: one quick retry (1.5 s) absorbs a true transient blip — the kind
 * of network microblip that is over before the user notices. Anything longer
 * surfaces as an error so the React error boundary renders the styled
 * "Something went wrong / Try again" screen instead of a frozen helmet or
 * an unstyled white page (the May-2026 incident).
 *
 * We deliberately do NOT run a long backoff here: load() blocks the page
 * render, and a 60 s wait staring at the helmet is worse than a clear
 * "Couldn't load — Retry" screen with user agency.
 *
 * 404 is permanent — propagated as ThreadNotFoundError on the first failure.
 */
export async function loadThreadMessages(
  remoteId: string,
  deps: LoadThreadMessagesDeps,
): Promise<ExportedMessageRepository & { unstable_resume?: boolean }> {
  const sleep = deps.sleep ?? ((ms) => new Promise<void>((r) => setTimeout(r, ms)));

  // A fresh load() means the React tree is being rebuilt for this thread
  // (page refresh, tab open, thread navigation). The in-memory message.parts
  // array that the cursor was anchored against is gone, so sending that
  // cursor back on /chat/resume would make the server skip events the
  // client never actually rendered — leaving the Analysis pane partial.
  deps.clearLastEventId(remoteId);

  const cachedLeafId = deps.getLastViewedBranch(remoteId);
  let lastErr: unknown;

  for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
    try {
      const res = await deps.apiAuthFetch(`api/threads/${remoteId}/messages`);
      if (res.status === 404) throw new ThreadNotFoundError(remoteId);
      if (!res.ok) throw new Error(`Failed to load thread: ${res.status}`);

      const envelope: {
        messages: ThreadHistoryItem[];
        isResponseCompleted: boolean;
        contextWindow: ContextWindowSnapshot | null;
        lastViewedMessageId: string | null;
      } = await res.json();
      const msgs = envelope.messages;

      deps.setContextWindow(remoteId, envelope.contextWindow ?? null);

      const allDocIds = msgs.flatMap((m) => m.generatedDocumentIds ?? []);
      if (allDocIds.length > 0) {
        await deps.fetchGeneratedDocuments(allDocIds);
      }

      // Hydrate Praetor revision store from persisted save_revision tool-call parts.
      const revisionStore = usePraetorRevisionStore.getState();
      revisionStore.clear();
      for (const msg of msgs) {
        if (msg.role !== "assistant") continue;
        for (const part of msg.parts) {
          if (part.type === "tool_call" && part.functionName === "save_revision" && part.arguments) {
            try {
              const args = JSON.parse(part.arguments);
              if (args.praetorDocumentId) {
                revisionStore.add({
                  praetorDocumentId: args.praetorDocumentId,
                  title: (part as { toolTitle?: string }).toolTitle ?? undefined,
                });
              }
            } catch { /* malformed args — skip */ }
          }
        }
      }

      const messageIds = new Set(msgs.map((m) => m.id));
      const candidateLeafId = envelope.lastViewedMessageId ?? cachedLeafId ?? null;
      const resolvedLeafId =
        candidateLeafId && messageIds.has(candidateLeafId) ? candidateLeafId : null;

      if (envelope.lastViewedMessageId !== cachedLeafId) {
        deps.setLastViewedBranch(remoteId, envelope.lastViewedMessageId ?? null);
      } else if (cachedLeafId !== null && resolvedLeafId === null) {
        deps.setLastViewedBranch(remoteId, null);
      }

      deps.clearLoadingFlag();

      // Phase 2 cold-reload approval re-hydration (notes-approval-flow-fixes.md §N2).
      // Fire-and-forget: the chat history paints immediately; the sidebar opens
      // a beat later if the thread has a pending request_commit_approval the backend
      // store still remembers (in-process within TTL; Phase 3 lifts to DB).
      void hydrateApprovalStoreFromHistory(remoteId, msgs, {
        apiAuthFetch: deps.apiAuthFetch as unknown as (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
      });

      // Topological sort guarantee: ensure parents always precede their children
      // in the array regardless of server ordering. Without this, same-timestamp
      // messages or an ordering bug would cause assistant-ui's sequential import()
      // to throw "Parent message not found".
      const sorted = topologicalSort(msgs);

      return {
        headId: resolvedLeafId,
        messages: sorted.map((m) => ({
          message: mapHistoryItemToThreadMessage(m),
          parentId: m.parentId,
        })),
        unstable_resume: !envelope.isResponseCompleted,
      };
    } catch (err) {
      // Permanent: propagate to error boundary (renders the styled
      // ThreadNotFoundError branch in ChatErrorFallback).
      if (err instanceof ThreadNotFoundError) {
        deps.clearLoadingFlag();
        throw err;
      }

      lastErr = err;
      ClientLogger.warn("Thread load attempt failed", {
        component: "loadThreadMessages",
        action: "GET /threads/{id}/messages",
        metadata: {
          attempt,
          threadId: remoteId,
          message: err instanceof Error ? err.message : String(err),
        },
      });

      if (attempt < MAX_LOAD_ATTEMPTS) {
        await sleep(LOAD_RETRY_DELAY_MS);
        continue;
      }

      deps.clearLoadingFlag();
      throw lastErr;
    }
  }

  // Unreachable — the loop either returns successfully or throws.
  throw lastErr;
}

/**
 * Topological sort: ensures parents always precede their children in the array.
 * Handles cycles gracefully (breaks them by appending remaining nodes in original order).
 * This is a defense-in-depth guarantee — the server already orders by CreatedAt + Id,
 * but if a bug or same-timestamp coincidence puts a child before its parent, assistant-ui's
 * sequential import() throws "Parent message not found".
 */
export function topologicalSort(messages: ThreadHistoryItem[]): ThreadHistoryItem[] {
  if (messages.length <= 1) return messages;

  const byId = new Map<string, ThreadHistoryItem>();
  for (const m of messages) byId.set(m.id, m);

  const result: ThreadHistoryItem[] = [];
  const visited = new Set<string>();

  function visit(m: ThreadHistoryItem) {
    if (visited.has(m.id)) return;
    visited.add(m.id);

    // Visit parent first (if it exists in the set)
    if (m.parentId) {
      const parent = byId.get(m.parentId);
      if (parent) visit(parent);
    }

    result.push(m);
  }

  for (const m of messages) visit(m);

  return result;
}
